import type { ReactNode } from "react";
import {
  Checkbox,
  cn,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "../ui";
import { ComboBox } from "../common/combo-box";
import { NodeTableColumnFormField } from "./custom/node-table-column-form-field";
import { NodeMapSettingFormField } from "./custom/node-map-setting-form-field";
import type { UseFormReturn } from "react-hook-form";
import LocationPickerDialog from "../common/location-picker-dialog";
import type { LatLngLiteral } from "leaflet";
import { getHexInputOptions } from "~/utils/form-util";
import type { FormOption, FormStructure } from "~/utils/form-util";

export enum FormFieldType {
  TEXT = "text",
  NUMBER = "number",
  EMAIL = "email",
  SELECT = "select",
  TEXTAREA = "textarea",
  CHECKBOX = "checkbox",
  DATE = "date",
  FILE = "file",
  PASSWORD = "password",
  COMBOBOX = "combobox",

  // Custom Types For Specific Use Cases
  CORDINATES = "cords",

  // Node Specific Types
  HEX = "hex",
  NODETYPE = "nodetype",

  // Setting specific types
  SETTING_NODE_TABLE_COLUMNS = "setting_node_table_columns",
  SETTING_NODE_MAP = "setting_node_map",
  SETTING_CORDINATES = "setting_cordinates",
}

export function getField<T>(
  t: (key: string) => string,
  s: FormStructure<T>,
  k: keyof T | "virtualFields",
  field: any,
  form: UseFormReturn<any>,
): ReactNode {
  let tp = s[k]?.type;

  // This makes it possible to modify the type based on other fields and values in the form
  // E.g. show a hex input field if the node type is lorawan but a text field if it is anything else
  if (s[k]?.modifyTypeFn) {
    tp = s[k].modifyTypeFn(form);
  }

  // Handle virtual fields
  if (k === "virtualFields" && s.virtualFields) {
    const vf = s.virtualFields.find((v) => v.name === field.name);
    if (vf) {
      tp = vf.type;
    }
  }

  let disabled = s[k]?.disabled ?? false;
  if (s[k]?.disabledFn) {
    disabled = s[k]?.disabledFn(form, field) ?? false;
  }

  const placeholder = s[k]?.placeholder ? t(s[k].placeholder) : "";
  switch (tp) {
    case FormFieldType.TEXT:
      return (
        <Input
          id={field.id}
          className={s[k]?.className ?? ""}
          key={field.id}
          placeholder={placeholder}
          {...field}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.EMAIL:
      return (
        <Input
          id={field.id}
          key={field.id}
          type="email"
          placeholder={placeholder}
          className={s[k]?.className ?? ""}
          {...field}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.NUMBER:
      return (
        <Input
          id={field.id}
          key={field.id}
          type="number"
          className={s[k]?.className ?? ""}
          placeholder={placeholder}
          {...field}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.PASSWORD:
      return (
        <Input
          id={field.id}
          key={field.id}
          type="password"
          placeholder="**********"
          className={s[k]?.className ?? ""}
          {...field}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.CHECKBOX:
      return (
        <Checkbox
          key={field.id}
          id={field.id}
          value={field.value}
          onCheckedChange={field.onChange}
          className={s[k]?.className ?? ""}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.TEXTAREA:
      return (
        <textarea
          id={field.id}
          key={field.id}
          placeholder={placeholder}
          className={
            "w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" +
            (s[k]?.className ? " " + s[k].className : "")
          }
          {...field}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.COMBOBOX:
      return (
        <ComboBox
          key={field.id}
          options={(s[k].options as FormOption[]) ?? []}
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          className={s[k]?.className ?? ""}
          placeholder={placeholder}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.CORDINATES: {
      // Use virtualFields to figure out mapping
      const vf =
        k === "virtualFields" && s.virtualFields
          ? s.virtualFields.find((v) => v.name === field.name)
          : undefined;

      const latKey =
        vf?.keys?.find(
          (d) =>
            d.toString().toLowerCase() === "latitude" ||
            d.toString().toLowerCase() === "lat",
        ) ?? null;

      const lonKey =
        vf?.keys?.find(
          (d) =>
            d.toString().toLowerCase() === "longitude" ||
            d.toString().toLowerCase() === "lon" ||
            d.toString().toLowerCase() === "lng",
        ) ?? null;

      if (!latKey || !lonKey) {
        console.error("No lat/lon keys found in virtual field", vf);
        return <div>Invalid coordinate field</div>;
      }

      if (form === null) return <div>Assert Failed</div>;

      const control = form.control;
      return (
        <div className="flex gap-2">
          <FormField
            control={control as any}
            name={latKey as any}
            render={({ field: f }) => (
              <Input
                id={field.id + "_lat"}
                type="number"
                step="any"
                placeholder="Latitude"
                className={s[k]?.className ?? ""}
                {...f}
                disabled={disabled ?? false}
              />
            )}
          />
          <FormField
            control={control as any}
            name={lonKey as any}
            render={({ field: f }) => (
              <Input
                id={field.id + "_lon"}
                type="number"
                step="any"
                placeholder="Longitude"
                className={s[k]?.className ?? ""}
                {...f}
                disabled={disabled ?? false}
              />
            )}
          />
          <LocationPickerDialog
            latitude={(form?.getValues(latKey as any) as number) ?? null}
            longitude={(form?.getValues(lonKey as any) as number) ?? null}
            onChange={(cords: LatLngLiteral | null) => {
              if (cords) {
                form?.setValue(latKey as any, cords.lat as any, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                form?.setValue(lonKey as any, cords.lng as any, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }
            }}
          />
        </div>
      );
    }

    case FormFieldType.SETTING_NODE_TABLE_COLUMNS:
      return (
        <NodeTableColumnFormField
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          options={(s[k].options as { label: string; value: any }[]) ?? []}
        />
      );

    case FormFieldType.SETTING_NODE_MAP:
      return (
        <NodeMapSettingFormField
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          options={(s[k].options as FormOption[]) ?? []}
        />
      );

    case FormFieldType.HEX:
      let hexBytes = 8;
      if (s[k].options) hexBytes = getHexInputOptions(s[k].options);

      const maxChars = hexBytes * 2;
      return (
        <Input
          {...field}
          placeholder={placeholder || "Enter identifier"}
          maxLength={maxChars}
          onChange={(e) => {
            const input = e.target.value.replace(/[^0-9a-fA-F]/g, "");
            field.onChange(input.toUpperCase());
            field.shouldValidate = true;
          }}
          className={
            cn("font-mono tracking-wider", field.error && "border-red-500") +
            (s[k]?.className ? " " + s[k].className : "")
          }
          disabled={disabled ?? false}
        />
      );

    default:
      return undefined;
  }
}

export function generateFields<T>(
  form: UseFormReturn<Record<keyof T, any>, any, Record<keyof T, any>>,
  structure: FormStructure<T>,
  t: (key: string) => string,
): ReactNode[] {
  const fields: ReactNode[] = [];

  // Collect keys that are covered by virtual fields
  const coveredKeys = new Set<keyof T>();
  structure.virtualFields?.forEach((vf) =>
    vf.keys.forEach((k) => coveredKeys.add(k)),
  );

  for (const [key, def] of Object.entries(structure) as [
    keyof T | "virtualFields",
    any,
  ][]) {
    if (key === "virtualFields") continue;
    if (coveredKeys.has(key as keyof T)) continue;
    if (def.hidden) continue;

    fields.push(
      <FormField
        key={key as string}
        control={form.control}
        name={key as any}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t(def.label)}</FormLabel>
            <FormControl>
              {getField(t, structure, key, field, form)}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />,
    );
  }

  // Render virtual fields
  structure.virtualFields?.forEach((vf) => {
    fields.push(
      <FormField
        key={vf.name}
        control={form.control}
        name={vf.name as any}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t(vf.label)}</FormLabel>
            <FormControl>
              {getField(t, structure, "virtualFields", field, form)}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />,
    );
  });

  return fields;
}
